import React, { useEffect, useRef, useState } from "react";
import { CheckCircle, AlertCircle, Loader2, MapPin } from "lucide-react";
import { fetchCountries, fetchRegions, fetchProvinces, searchCities } from "../utils/geoApi";

const INPUT_CLASS = "w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#028CF8]/30 focus:border-[#028CF8]";
const ADMIN1_LABEL = { comunidadAutonoma: "Comunidad Autónoma", departamento: "Departamento", estado: "Estado", provincia: "Provincia" };
const ADMIN1_PLACEHOLDER = { comunidadAutonoma: "Selecciona una comunidad autónoma", departamento: "Selecciona un departamento", estado: "Selecciona un estado", provincia: "Selecciona una provincia" };
export const COUNTRY_NAMES_ES = { ES: "España", UY: "Uruguay", US: "Estados Unidos", AR: "Argentina", BR: "Brasil" };

/** Admin-only equivalent of the public LocationSelector (no LanguageContext
 * dependency — the /admin tree is intentionally isolated). Same backend geo
 * endpoints/utils, same País → Región → Provincia(ES) → Ciudad UX. */
export function AdminLocationSelector({
  countryCode, admin1Code, admin1Name, admin2Code, admin2Name, city, locationVerified, onChange,
}) {
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cityQuery, setCityQuery] = useState(city || "");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { fetchCountries().then(setCountries).catch(() => {}); }, []);
  useEffect(() => {
    if (!countryCode) { setRegions([]); return; }
    fetchRegions(countryCode).then(setRegions).catch(() => setRegions([]));
  }, [countryCode]);
  useEffect(() => {
    if (countryCode !== "ES" || !admin1Code) { setProvinces([]); return; }
    fetchProvinces(countryCode, admin1Code).then(setProvinces).catch(() => setProvinces([]));
  }, [countryCode, admin1Code]);
  useEffect(() => { setCityQuery(city || ""); }, [city]);

  const countryMeta = countries.find((c) => c.code === countryCode);
  const level1Key = countryMeta?.level1_label_key;
  const regionNameForSearch = countryCode === "ES" ? (admin2Name || admin1Name) : admin1Name;
  const needsAdmin2Step = countryCode === "ES" && provinces.length > 0;
  const cityEnabled = !!countryCode && (needsAdmin2Step ? !!admin2Code : !!admin1Code);

  const handleCountryChange = (e) => {
    const code = e.target.value || null;
    onChange({ country_code: code, admin1_code: null, admin1_name: null, admin2_code: null, admin2_name: null, city: null, latitude: null, longitude: null, location_verified: false });
    setCityQuery(""); setSuggestions([]);
  };
  const handleAdmin1Change = (e) => {
    const code = e.target.value;
    const region = regions.find((r) => r.code === code);
    onChange({ admin1_code: code || null, admin1_name: region?.name || null, admin2_code: null, admin2_name: null, city: null, latitude: null, longitude: null, location_verified: false });
    setCityQuery(""); setSuggestions([]);
  };
  const handleAdmin2Change = (e) => {
    const code = e.target.value;
    const province = provinces.find((p) => p.code === code);
    onChange({ admin2_code: code || null, admin2_name: province?.name || null, city: null, latitude: null, longitude: null, location_verified: false });
    setCityQuery(""); setSuggestions([]);
  };
  const handleCityInput = (e) => {
    const val = e.target.value;
    setCityQuery(val);
    onChange({ city: val, latitude: null, longitude: null, location_verified: false });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 3 || !countryCode) { setSuggestions([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await searchCities(countryCode, val.trim(), regionNameForSearch);
        setSuggestions(results);
        setShowDropdown(true);
      } catch { setSuggestions([]); }
      finally { setSearching(false); }
    }, 350);
  };
  const pickSuggestion = (s) => {
    setCityQuery(s.city);
    setShowDropdown(false);
    onChange({ city: s.city, latitude: s.latitude, longitude: s.longitude, location_verified: true, admin1_name: s.admin1_name || admin1Name });
  };

  return (
    <div className="space-y-3" data-testid="admin-location-selector">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-500 mb-1 block">País</label>
          <select data-testid="admin-job-country-select" value={countryCode || ""} onChange={handleCountryChange} className={INPUT_CLASS}>
            <option value="">Selecciona un país</option>
            {countries.map((c) => <option key={c.code} value={c.code}>{COUNTRY_NAMES_ES[c.code] || c.code}</option>)}
          </select>
        </div>
        {countryCode && (
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">{ADMIN1_LABEL[level1Key] || "Región"}</label>
            <select data-testid="admin-job-admin1-select" value={admin1Code || ""} onChange={handleAdmin1Change} className={INPUT_CLASS}>
              <option value="">{ADMIN1_PLACEHOLDER[level1Key] || "Selecciona una región"}</option>
              {regions.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
            </select>
          </div>
        )}
        {needsAdmin2Step && (
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Provincia</label>
            <select data-testid="admin-job-admin2-select" value={admin2Code || ""} onChange={handleAdmin2Change} className={INPUT_CLASS}>
              <option value="">Selecciona una provincia</option>
              {provinces.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
            </select>
          </div>
        )}
      </div>
      {countryCode && (
        <div className="relative">
          <label className="text-xs font-semibold text-slate-500 mb-1 block">Ciudad / Localidad</label>
          <div className="relative">
            <input
              data-testid="admin-job-city-input"
              value={cityQuery}
              disabled={!cityEnabled}
              onChange={handleCityInput}
              onFocus={() => { if (suggestions.length) setShowDropdown(true); }}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="Empieza a escribir para buscar..."
              className={`${INPUT_CLASS} pr-9 disabled:bg-slate-50 disabled:text-slate-400`}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {searching ? <Loader2 className="h-4 w-4 text-slate-400 animate-spin" /> :
                locationVerified ? <CheckCircle className="h-4 w-4 text-green-500" /> :
                cityQuery ? <AlertCircle className="h-4 w-4 text-amber-500" /> : null}
            </span>
          </div>
          {showDropdown && (
            <div data-testid="admin-job-city-suggestions" className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
              {suggestions.length > 0 ? suggestions.map((s, i) => (
                <button type="button" key={`${s.city}-${i}`} onClick={() => pickSuggestion(s)}
                  className="w-full text-left px-3.5 py-2.5 text-sm hover:bg-[#028CF8]/5 flex items-center gap-2 border-b border-slate-50 last:border-0">
                  <MapPin className="h-3.5 w-3.5 text-[#028CF8] flex-shrink-0" />
                  <span className="truncate">{s.city}{s.admin1_name ? `, ${s.admin1_name}` : ""}</span>
                </button>
              )) : <p className="px-3.5 py-2.5 text-xs text-slate-600">No encontramos coincidencias. Puedes escribir la ubicación igualmente.</p>}
            </div>
          )}
          <p className="text-xs text-slate-600 mt-1">
            {locationVerified ? <span className="text-green-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Ubicación verificada</span>
              : cityQuery ? <span className="text-amber-600 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Sin verificar en el mapa</span>
              : "Selecciona una sugerencia para verificar la ubicación en el mapa."}
          </p>
        </div>
      )}
    </div>
  );
}
